import { PageBlockEditorModel } from "./model/block";
import {
  PageBlockDefinition,
  PageBlockDefinitionPropertyType,
  PageBlockItemEmbedValue,
  PageBlockItemMultiRichTextValue,
  PageBlockItemRichTextValue,
  PageBlockItemTableValue,
  PageBlockItemUntypedValue,
  PageBlockItemV2,
  PageBlockText,
} from "@supernova-studio/model";

export const BlockParsingUtils = {
  singleBlockItem(block: PageBlockEditorModel) {
    if (!block.data.items.length) throw new Error(`Block ${block.id} has no items`);
    if (block.data.items.length > 1) throw new Error(`Block ${block.id} has more than 1 item`);

    return block.data.items[0]!;
  },

  richTextPropertyValue(item: PageBlockItemV2, propertyKey: string): PageBlockItemRichTextValue {
    const objectValue = this.objectPropertyValue(item, propertyKey);
    const richText = PageBlockItemRichTextValue.parse(objectValue);

    return richText;
  },

  multiRichTextPropertyValue(item: PageBlockItemV2, propertyKey: string): PageBlockItemMultiRichTextValue {
    const objectValue = this.objectPropertyValue(item, propertyKey);
    const richText = PageBlockItemMultiRichTextValue.parse(objectValue);

    return richText;
  },

  tablePropertyValue(item: PageBlockItemV2, propertyKey: string): PageBlockItemTableValue {
    const objectValue = this.objectPropertyValue(item, propertyKey);
    const table = PageBlockItemTableValue.parse(objectValue);

    return table;
  },

  embedPropertyValue(item: PageBlockItemV2, propertyKey: string): PageBlockItemEmbedValue {
    const objectValue = this.objectPropertyValue(item, propertyKey);
    const embed = PageBlockItemEmbedValue.parse(objectValue);

    return embed;
  },

  objectPropertyValue(item: PageBlockItemV2, propertyKey: string) {
    const value = this.propertyValueOrThrow(item, propertyKey);

    if (typeof value !== "object") {
      throw new Error(`Value for property ${propertyKey} is expected to be an object, but instead is ${typeof value}`);
    }

    return value;
  },

  propertyValueOrThrow(item: PageBlockItemV2, propertyKey: string): PageBlockItemUntypedValue {
    const value = item.props[propertyKey];
    if (!value) throw new Error(`Property ${propertyKey} is not defined on block item`);

    return value;
  },
};

export const BlockDefinitionUtils = {
  firstRichTextProperty(definition: PageBlockDefinition) {
    const property = definition.item.properties.find(p => p.type === "RichText");
    if (property) return property;
    return undefined;
  },

  firstMultiRichTextProperty(definition: PageBlockDefinition) {
    const property = definition.item.properties.find(p => p.type === "MultiRichText");
    if (property) return property;
    return undefined;
  },

  firstTableProperty(definition: PageBlockDefinition) {
    const property = definition.item.properties.find(p => p.type === "Table");
    if (property) return property;
    return undefined;
  },

  firstEmbedProperty(definition: PageBlockDefinition) {
    const property = definition.item.properties.find(p => p.type === "EmbedURL");
    if (property) return property;
    return undefined;
  },

  richTextProperty(definition: PageBlockDefinition, propertyKey: string) {
    return this.property(definition, propertyKey, "RichText");
  },

  property(
    definition: PageBlockDefinition,
    propertyKey: string,
    expectedPropertyType?: PageBlockDefinitionPropertyType
  ) {
    const property = definition.item.properties.find(p => p.id === propertyKey);
    if (!property) {
      throw new Error(`Definition ${definition.id} doesn't contain property ${propertyKey}`);
    }

    if (expectedPropertyType && property.type !== expectedPropertyType) {
      throw new Error(
        `Property ${propertyKey} of definition ${definition.id} expected to be ${expectedPropertyType} but was ${property.type}`
      );
    }

    return property;
  },
};
